/**
 * EVO — ko'rsatish uchun formatlash.
 *
 * ============================================================================
 * ENG MUHIM QOIDA: PUL API'DAN `string` BO'LIB KELADI VA `string` BO'LIB QOLADI.
 * ============================================================================
 *
 * Postgres `numeric(18,4)` ni JSON'ga `"14977.4100"` ko'rinishida qaytaradi.
 * `parseFloat("14977.4100")` — IEEE 754 float, ya'ni `0.1 + 0.2 !== 0.3`.
 * Bitta ekranda bu ko'rinmaydi; 5 000 satrli ombor qiymati hisobotida
 * buxgalteriya bilan tiyinlab farq chiqadi va sababini oylab qidiriladi.
 *
 * Shuning uchun quyidagi funksiyalar RAQAMGA UMUMAN AYLANTIRMAYDI —
 * yaxlitlash ham, ming ajratish ham satr ustida bajariladi.
 * `Number()`, `parseFloat()`, `toLocaleString()` bu faylda TAQIQLANGAN.
 *
 * Arifmetika (qo'shish, ko'paytirish, taqsimlash) brauzerda umuman qilinmaydi —
 * u serverda `@evo/core` (Decimal.js) orqali bajariladi va tayyor natija keladi.
 *
 * Asos: EVO_GAP_ANALYSIS_v1.md §C4, EVO_DATA_MODEL_v1.md §1.3
 */

/** API'dan kelgan pul qiymati. Ataylab `string` — `number` emas. */
export type MoneyString = string;

/**
 * ╔══════════════════════════════════════════════════════════════════════════╗
 * ║ MING AJRATGICH — TOR UZILMAS BO'SHLIQ (U+202F), ODDIY BO'SHLIQ EMAS      ║
 * ║                                                                          ║
 * ║ Vergul (`1,000,000`) ilgari rad etilgan va u RAD ETILGANICHA QOLADI:     ║
 * ║ GAP_ANALYSIS §C4 — `1 000 000` O'zbekistonda odatiy yozuv. O'zgargan     ║
 * ║ narsa — BO'SHLIQNING TURI, ajratgichning o'zi emas.                      ║
 * ║                                                                          ║
 * ║ Oddiy bo'shliq (U+0020) ikki xil zarar keltirardi va ikkalasi ham jonli  ║
 * ║ ekranda ko'rindi:                                                        ║
 * ║                                                                          ║
 * ║  1. KENGLIK. Raqamlar `--font-mono` bilan chiziladi va monoshriftda      ║
 * ║     bo'shliq HAR QANDAY raqam bilan bir xil kenglikda — ya'ni to'liq     ║
 * ║     bitta katak. `$173 071.00` ko'zga bitta summa emas, IKKI SON bo'lib  ║
 * ║     tushardi: «$173» va «071.00». U+202F esa taxminan yarim katak.       ║
 * ║                                                                          ║
 * ║  2. BO'LINISH. Oddiy bo'shliq — satr ko'chirish IMKONIYATI. Skrinshotda  ║
 * ║     jadval katagida `$7 059.89` ikkiga bo'linib, `$7` yuqori qatorda,    ║
 * ║     `059.89` pastki qatorda turgan edi — ya'ni ekranda mavjud bo'lmagan  ║
 * ║     ikkita raqam. U+202F (NARROW NO-BREAK SPACE) UZILMAYDI, ya'ni bu     ║
 * ║     endi CSS ga emas, matnning o'ziga bog'liq.                           ║
 * ║                                                                          ║
 * ║ `.evo-num { white-space: nowrap }` (styles.css) QOLADI — u ustundagi     ║
 * ║ tekislashni va shriftni beradi. Lekin u endi yagona himoya emas: pul     ║
 * ║ kartochkada, ogohlantirishda va tarjima matni ichida ham chiqadi, u      ║
 * ║ yerda esa hech qanday `.evo-num` yo'q.                                   ║
 * ║                                                                          ║
 * ║ ⚠️ ILGARIGI E'TIROZ («nusxa ko'chirilgan raqam boshqa dasturga ko'rinmas ║
 * ║ belgi bilan tushadi») BEKOR QILINDI, chunki uning asosi yo'qoldi:        ║
 * ║ ma'lumot ekrandan Excel'ga NUSXA orqali emas, `export-xlsx.ts` orqali    ║
 * ║ chiqadi va u XOM sonni yozadi (`"14977.4100"`), formatlangan matnni      ║
 * ║ emas. Ya'ni eksport yo'lida bu belgi UMUMAN paydo bo'lmaydi.             ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 */
const GROUP_SEPARATOR = '\u202f';
const DECIMAL_SEPARATOR = '.';

/**
 * ╔══════════════════════════════════════════════════════════════════════════╗
 * ║ MANFIY ISHORA — HAQIQIY MINUS (U+2212), DEFIS EMAS                       ║
 * ║                                                                          ║
 * ║ Klaviaturadagi `-` — bu DEFIS-MINUS: u raqam balandligidan past turadi   ║
 * ║ va qisqa, ya'ni `$-212 787.40` da uni ko'z tire yoki chiziqcha deb       ║
 * ║ o'qiydi. U+2212 esa `+` bilan bir balandlik va bir kenglikda.            ║
 * ║                                                                          ║
 * ║ Ishora VALYUTA BELGISIDAN OLDIN turadi — `−$212 787.40`. `$-212 787.40`  ║
 * ║ deb hech bir buxgalter yozmaydi: ishora BUTUN summaga tegishli, dollarga ║
 * ║ emas. Bu qavs ichida yozish (`($212 787.40)`) ham RAD ETILDI: u          ║
 * ║ amerikacha odat va bu yerda hech kim uni manfiy deb o'qimaydi.           ║
 * ║                                                                          ║
 * ║ Butun belgi UZILMAYDI va buning uchun qo'shimcha belgi kerak emas:       ║
 * ║ UAX #14 bo'yicha `−` ham, `$` ham prefiks sinfi (PR) va ulardan keyin    ║
 * ║ raqam kelganda satr ko'chirilmaydi; ming ajratgichlar esa U+202F.        ║
 * ║                                                                          ║
 * ║ ⚠️ BU FAQAT KO'RSATISH UCHUN. Hisob-kitob va eksportdagi qiymat XOM      ║
 * ║ satr bo'lib qoladi (`"-212787.4000"`) — kod hech qachon shu yerdan       ║
 * ║ chiqqan matnni qayta o'qimaydi (fayl boshidagi qoida).                   ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 */
const MINUS = '−';

/**
 * Summa bilan valyuta SO'ZI orasidagi bo'shliq — uzilmas (U+00A0).
 *
 * Bu yerda U+202F ishlatilmaydi: tor bo'shliq raqamlar ichida to'g'ri, so'z
 * bilan raqam orasida esa ularni bir-biriga yopishtirib qo'yadi
 * (`180 733so'm` degandek o'qiladi). Kerak bo'lgani — odatdagi kenglik,
 * lekin bo'linmaydigan: `180 733` va `so'm` ikki qatorga tushsa, birinchi
 * qator valyutasiz summaga aylanadi.
 */
const CURRENCY_SPACE = '\u00a0';

interface NumericParts {
  readonly sign: '' | '-';
  readonly int: string;
  readonly frac: string;
}

/** `"-1234.5600"` → `{ sign: '-', int: '1234', frac: '5600' }`. Raqamga aylantirmaydi. */
function split(raw: string): NumericParts {
  const trimmed = raw.trim();
  const negative = trimmed.startsWith('-');
  const body = negative ? trimmed.slice(1) : trimmed.replace(/^\+/, '');
  const dot = body.indexOf(DECIMAL_SEPARATOR);
  const intRaw = dot === -1 ? body : body.slice(0, dot);
  const frac = dot === -1 ? '' : body.slice(dot + 1);

  // Faqat raqamlardan iborat bo'lishi kerak; aks holda qiymat buzilgan.
  const int = intRaw.replace(/^0+(?=\d)/, '') || '0';
  return { sign: negative ? '-' : '', int, frac };
}

/** `"1299"` → `"1300"`. Satr ustida bir qadam oshirish (yaxlitlash uchun). */
function incrementDigits(digits: string): string {
  const out = digits.split('');
  for (let i = out.length - 1; i >= 0; i -= 1) {
    if (out[i] === '9') {
      out[i] = '0';
    } else {
      out[i] = String(Number(out[i]) + 1);
      return out.join('');
    }
  }
  return `1${out.join('')}`;
}

/**
 * Belgilangan xonagacha yaxlitlash — half-up (O'zbekiston/rus buxgalteriya amaliyoti),
 * satr arifmetikasi bilan. `Number` ishlatilmaydi.
 */
function roundString(raw: string, scale: number): NumericParts {
  const { sign, int, frac } = split(raw);

  if (frac.length <= scale) {
    return { sign, int, frac: frac.padEnd(scale, '0') };
  }

  const keep = frac.slice(0, scale);
  const nextDigit = frac.charAt(scale);
  let digits = int + keep;

  if (nextDigit >= '5') {
    digits = incrementDigits(digits);
  }

  const intLength = digits.length - scale;
  const newInt = (intLength > 0 ? digits.slice(0, intLength) : '0').replace(/^0+(?=\d)/, '') || '0';
  const newFrac = intLength > 0 ? digits.slice(intLength) : digits.padStart(scale, '0');

  return { sign, int: newInt, frac: newFrac };
}

function group(int: string): string {
  return int.replace(/\B(?=(\d{3})+(?!\d))/g, GROUP_SEPARATOR);
}

/** Manfiy nol ko'rinmasligi kerak: `-0.00` → `0.00`. */
function isAllZero(parts: NumericParts): boolean {
  return /^0*$/.test(parts.int) && /^0*$/.test(parts.frac);
}

/**
 * Sonni ko'rsatish uchun formatlaydi: `1234567.891` → `1 234 567.89`
 * (ajratgich — U+202F, yuqoridagi ramkaga qarang).
 * Manfiy son `−1 234.56` bo'lib chiqadi — ishora U+2212.
 * Yaroqsiz/bo'sh qiymat uchun `—` qaytaradi (ekranda `NaN` chiqmaydi).
 */
export function formatNumber(value: MoneyString | null | undefined, scale = 2): string {
  if (value === null || value === undefined || value === '') return EMPTY_VALUE;
  if (!/^[+-]?\d*(\.\d*)?$/.test(value.trim())) return EMPTY_VALUE;

  const parts = roundString(value, scale);
  const sign = isAllZero(parts) ? '' : parts.sign === '-' ? MINUS : '';
  const body = parts.frac.length > 0 ? `${group(parts.int)}${DECIMAL_SEPARATOR}${parts.frac}` : group(parts.int);
  return `${sign}${body}`;
}

export const EMPTY_VALUE = '—';

export type CurrencyCode = 'USD' | 'UZS' | 'CNY' | (string & {});

/**
 * Valyuta bo'yicha xona soni.
 *
 * UZS uchun 0 — so'mda tiyin amalda ishlatilmaydi va `180 733.00 so'm`
 * ekranni ma'nosiz uzaytiradi. USD/CNY uchun 2.
 * Kurs (`rate`) buning tashqarisida — u `formatRate` bilan formatlanadi.
 */
function scaleFor(currency: CurrencyCode): number {
  return currency === 'UZS' ? 0 : 2;
}

/**
 * Pulni valyuta bilan formatlaydi.
 *
 *   formatMoney('14.1234',    'USD') → `$14.12`
 *   formatMoney('-212787.40', 'USD') → `−$212 787.40`
 *   formatMoney('180733',     'UZS') → `180 733 so'm`
 *   formatMoney('45',         'CNY') → `¥45.00`
 *
 * `value` — API'dan kelgan SATR. Uni `parseFloat` qilish taqiqlangan (fayl boshiga qarang).
 *
 * Ishora RAQAMDAN AJRATIB olinadi va valyuta belgisidan oldinga qo'yiladi
 * (`MINUS` ramkasidagi sabab). So'z bilan yoziladigan valyutalarda (`so'm`,
 * noma'lum kod) ishora baribir birinchi turadi, ya'ni shox bitta qoladi va
 * ikki xil qoida tug'ilmaydi.
 */
export function formatMoney(
  value: MoneyString | null | undefined,
  currency: CurrencyCode,
): string {
  const formatted = formatNumber(value, scaleFor(currency));
  if (formatted === EMPTY_VALUE) return EMPTY_VALUE;

  const negative = formatted.startsWith(MINUS);
  const sign = negative ? MINUS : '';
  const digits = negative ? formatted.slice(MINUS.length) : formatted;

  switch (currency) {
    case 'USD':
      return `${sign}$${digits}`;
    case 'UZS':
      return `${sign}${digits}${CURRENCY_SPACE}so'm`;
    case 'CNY':
      return `${sign}¥${digits}`;
    default:
      return `${sign}${digits}${CURRENCY_SPACE}${currency}`;
  }
}

/**
 * Valyuta kursi: `12800.00000000` → `12 800`.
 * Ortiqcha nollar olib tashlanadi — kurs ekran tepasida turadi, qisqa bo'lishi kerak.
 */
export function formatRate(value: MoneyString | null | undefined): string {
  if (value === null || value === undefined || value === '') return EMPTY_VALUE;
  const { frac } = split(value);
  const meaningful = frac.replace(/0+$/, '');
  return formatNumber(value, Math.min(meaningful.length, 4));
}

/** Miqdor: `1500.000` → `1 500`. Kasr bo'lsa ko'rsatiladi (`2.5 m`). */
export function formatQty(value: MoneyString | null | undefined): string {
  if (value === null || value === undefined || value === '') return EMPTY_VALUE;
  const { frac } = split(value);
  const meaningful = frac.replace(/0+$/, '');
  return formatNumber(value, Math.min(meaningful.length, 3));
}

/** Foiz: `33.912` → `33.9%` */
export function formatPercent(value: MoneyString | null | undefined, scale = 1): string {
  const formatted = formatNumber(value, scale);
  return formatted === EMPTY_VALUE ? EMPTY_VALUE : `${formatted}%`;
}

// ---------------------------------------------------------------------------
// Sana
// ---------------------------------------------------------------------------

function pad2(value: number): string {
  return value < 10 ? `0${value}` : String(value);
}

/**
 * `YYYY-MM-DD` (Postgres `date`) ni Date'ga aylantirmasdan ajratadi.
 *
 * NIMA UCHUN: `new Date('2026-08-08')` UTC yarim tunini beradi, Toshkent UTC+5 —
 * `getDate()` mahalliy vaqtda ba'zan 7-avgustni qaytaradi. Kunlik kurs va
 * hujjat sanasi uchun bu bir kunlik xato, ya'ni noto'g'ri kurs bo'yicha hisob.
 */
const PLAIN_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

export type DateInput = string | Date | null | undefined;

/**
 * `dd.MM.yyyy` — mahalliy yozuv. ISO (`2026-08-08`) EMAS:
 * hujjatlarda, chop etishda va 1C'da odatlangan format shu.
 */
export function formatDate(input: DateInput): string {
  if (input === null || input === undefined || input === '') return EMPTY_VALUE;

  if (typeof input === 'string') {
    const plain = PLAIN_DATE.exec(input);
    if (plain) {
      return `${plain[3]}.${plain[2]}.${plain[1]}`;
    }
  }

  const date = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(date.getTime())) return EMPTY_VALUE;

  return `${pad2(date.getDate())}.${pad2(date.getMonth() + 1)}.${date.getFullYear()}`;
}

/** `dd.MM.yyyy HH:mm` — audit jurnali va hujjat tarixida. */
export function formatDateTime(input: DateInput): string {
  if (input === null || input === undefined || input === '') return EMPTY_VALUE;

  const date = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(date.getTime())) return EMPTY_VALUE;

  return `${formatDate(date)} ${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
}

/**
 * Bugungi sana `YYYY-MM-DD` ko'rinishida, MAHALLIY vaqt zonasida.
 * `toISOString().slice(0, 10)` ishlatilmaydi — u UTC beradi va
 * Toshkentda ertalab soat 05:00 gacha kechagi sanani qaytaradi.
 */
export function todayIso(): string {
  const now = new Date();
  return `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`;
}

/** Sana bugungimi — kunlik kurs vidjeti uchun asosiy tekshiruv. */
export function isToday(dateIso: string | null | undefined): boolean {
  return !!dateIso && dateIso.slice(0, 10) === todayIso();
}
